import React, { ReactElement, useReducer, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import I18n from '../../setupI18n';
import { Alert, Button, Wizard } from '@amzn/awsui-components-react/polaris';
import {
  initialFormValuesStep1,
  ProjectCreateAPIStep1,
  ProjectCreateStep1Metadata,
  schemaStep1,
} from './ProjectCreateStep1Metadata';
import { AtmsApiClient, AuthenticatedSession } from '@amzn/et-console-components';
import { FormikProps } from 'formik';
import { PolarisFormik } from '@amzn/et-polaris-utils';
import * as yup from 'yup';
import { ProjectCreateSummary } from './ProjectCreateSummary';
import {
  getDefaultMachineTranslationEngine,
  initialFormValuesStep3,
  ProjectCreateAPIStep3,
  ProjectCreateStep3Pretranslate,
  schemaStep3,
} from './ProjectCreateStep3Pretranslate';
import {
  initialFormValuesStep2,
  ProjectCreateAPIStep2,
  ProjectCreateStep2Assets,
  schemaStep2,
} from './ProjectCreateStep2Assets';
import {
  initialFormValuesStep4,
  ProjectCreateAPIStep4,
  ProjectCreateStep4Analysis,
  schemaStep4,
} from './ProjectCreateStep4Analysis';
import {
  initialFormValuesStep5,
  ProjectCreateAPIStep5,
  ProjectCreateStep5Quality,
  schemaStep5,
} from './ProjectCreateStep5Quality';
import {
  initialFormValuesStep6,
  ProjectCreateAPIStep6,
  ProjectCreateStep6AccessAndAutomation,
  schemaStep6,
} from './ProjectCreateStep6AccessAndAutomation';
import { WizardStepWithSummary, WizardWithSummary } from '../components/WizardWithSummary';
import {
  jobCreateSchemaStep2,
  JobCreateStep2AssignJobs,
  jobCreateInitialFormValuesStep2,
  JobCreateAPIStep2,
} from './jobs/JobCreateStep2AssignJobs';
import {
  JobCreateAPIStep3,
  jobCreateInitialFormValuesStep3,
  jobCreateSchemaStep3,
  JobCreateStep3FileImporting,
} from './jobs/JobCreateStep3FileImporting';
import { Spinner } from '@amzn/awsui-components-react/polaris';
import { PROJECT_CREATE_REVIEW_AND_SAVE_HELP } from './projectHelpContent';
import { useWorkflowSteps } from '../resourceHooks/useWorkflowSteps';
import { useMediaTypes } from '../resourceHooks/useMediaTypes';
import { useSubjects } from '../resourceHooks/useSubjects';
import { useBusinessUnits } from '../resourceHooks/useBusinessUnits';
import { useClients } from '../resourceHooks/useClients';
import { useOwners } from '../resourceHooks/useOwners';
import { useCostCenters } from '../resourceHooks/useCostCenters';
import { useMachineTranslateSettings } from '../resourceHooks/useMachineTranslateSettings';
import { useActiveLanguages } from '../resourceHooks/useActiveLanguages';
import { useSupportedFileFormats } from '../resourceHooks/useSupportedFileFormats';
import { useSupportedFileEncodings } from '../resourceHooks/useSupportedFileEncodings';
import { useSegmentationRules } from '../resourceHooks/useSegmentationRules';
import { useUsersAndVendorsForJobAssign } from '../resourceHooks/useUsersAndVendorsForJobAssign';
import { useProject } from '../resourceHooks/useProject';
import { useProjectTemplate } from '../resourceHooks/useProjectTemplate';
import { useScoringModels } from '../resourceHooks/useScoringModels';
import { useTermBases } from '../resourceHooks/useTermBases';
import { saveTBConfigs } from './termbases/TermBaseCommons';
import { saveTMConfig } from './translationmemory/TranslationMemoryCommon';
import { useTranslationMemories } from '../resourceHooks/useTranslationMemories';
import { useReferences } from '../resourceHooks/useReferences';
import { createReferences } from './references/ReferenceCommons';
import { publishCountMetric, publishLatencyMetric } from '../metricHelper';

interface ReducerState {
  isCreatingProjectTemplate: boolean;
  isError: boolean;
  error?: string;
  activeStep: number;
}

interface ProjectTemplateCreateAPI
  extends ProjectCreateAPIStep1,
    ProjectCreateAPIStep2,
    ProjectCreateAPIStep3,
    ProjectCreateAPIStep4,
    ProjectCreateAPIStep5,
    ProjectCreateAPIStep6,
    JobCreateAPIStep2,
    JobCreateAPIStep3 {}

export interface Props extends RouteComponentProps {
  session: AuthenticatedSession;
}

export const ProjectTemplateCreate = withRouter(
  ({ history, location: { pathname }, session }: Props) => {
    // TODO: We should be able to get this from the Router.match.params prop
    const projectUID = pathname.endsWith('create')
      ? undefined
      : pathname.substr(pathname.lastIndexOf('/') + 1);

    const init = (): ReducerState => ({
      activeStep: 0,
      isCreatingProjectTemplate: false,
      isError: false,
    });

    const reducer = (state, action): ReducerState => {
      switch (action.type) {
        case 'activeStepChange':
          return {
            ...state,
            activeStep: action.activeStep,
          };
        case 'creatingProjectTemplate':
          return {
            ...state,
            isCreatingProjectTemplate: true,
          };
        case 'projectTemplateCreated':
          return {
            ...state,
            isCreatingProjectTemplate: false,
          };
        case 'projectTemplateCreationFailed':
          return {
            ...state,
            isCreatingProjectTemplate: false,
            isError: true,
            error: action.error,
          };
        case 'clearError':
          return {
            ...state,
            isError: false,
            error: undefined,
          };
        default:
          return state;
      }
    };

    const [reducerState, dispatch] = useReducer(reducer, null, init);
    const { isCreatingProjectTemplate, activeStep, isError, error } = reducerState;
    const [validating, setValidating] = useState(false);
    const { isLoadingProject, project } = useProject(projectUID);
    const { isLoadingProjectTemplate, projectTemplate } = useProjectTemplate(
      project?.projectTemplateId
    );
    const { isLoadingWorkflowSteps, workflowSteps, workflowStepsById } = useWorkflowSteps();
    const { isLoadingClients, clients, clientsById } = useClients();
    const { isLoadingOwners, owners, ownersById } = useOwners();
    const { isLoadingCostCenters, costCenters, costCentersById } = useCostCenters();
    const { isLoadingBusinessUnits, businessUnits, businessUnitsById } = useBusinessUnits();
    const { isLoadingMediaTypes, mediaTypes, mediaTypesById } = useMediaTypes();
    const { isLoadingSubjects, subjects, subjectsById } = useSubjects();
    const {
      isLoadingActiveLanguages,
      activeLanguages,
      activeLanguagesByCode,
    } = useActiveLanguages();
    const { isLoadingScoringModels, scoringModels, scoringModelsById } = useScoringModels();
    const {
      isLoadingMachineTranslateSettings,
      machineTranslateSettings,
      machineTranslateSettingsById,
    } = useMachineTranslateSettings();
    const { isLoadingUsers, foundUsers, usersLoadError } = useUsersAndVendorsForJobAssign(session);
    const combinedError = error ?? usersLoadError;
    const { isLoadingSupportedFileFormats, supportedFileFormats } = useSupportedFileFormats();
    const { isLoadingSupportedFileEncodings, supportedFileEncodings } = useSupportedFileEncodings();
    const { isLoadingSegmentationRules, segmentationRules } = useSegmentationRules();
    const { isLoadingTermBaseAssignments, termBaseAssignments } = useTermBases(true, projectUID);
    const {
      isLoadingTransMemoryAssignments,
      translationMemoryAssignments,
    } = useTranslationMemories(true, projectUID);
    const { isLoadingReferences, references } = useReferences(true, projectUID);

    const buildRequest = (values: ProjectTemplateCreateAPI): any => {
      if (!values.perLocaleMachineTranslation) {
        for (const [plMtSettingsId] of Object.entries(
          values.perLocaleMachineTranslateSettingsIds
        )) {
          values.perLocaleMachineTranslateSettingsIds[plMtSettingsId] = 0;
        }
        if (values.machineTranslateSettingsId === 0) {
          delete values.machineTranslateSettingsId;
        }
      }

      const originalValues = {
        ...values,
        name: (values.name ?? '').length === 0 ? values.projectTemplateName : values.name,
        note: values.note ? values.note : undefined, // It seems like the Textarea returns 'null' instead of 'undefined' for 'no value'
        perLocaleMachineTranslateSettingsIds: JSON.stringify(
          values.perLocaleMachineTranslateSettingsIds
        ),
        machineTranslateSettingsId: values.machineTranslateSettingsId,
        sourceSegmentationRuleId:
          values.sourceSegmentationRuleId?.toString() === 'undefined'
            ? undefined
            : values.sourceSegmentationRuleId,
        targetSegmentationRuleId:
          values.targetSegmentationRuleId?.toString() === 'undefined'
            ? undefined
            : values.targetSegmentationRuleId,
        // filter out the deleted workflow step
        workflowStepIds: values.workflowStepIds.filter(id => workflowStepsById[id] !== undefined),
      };
      const {
        termBaseAssignments,
        translationMemoryAssignments,
        references,
        ...request
      } = originalValues;
      return request;
    };

    const handleSubmit = (values: ProjectTemplateCreateAPI): void => {
      dispatch({ type: 'creatingProjectTemplate' });
      const initialTime = new Date();
      AtmsApiClient.httpPost('/api/v2/projectTemplate', buildRequest(values))
        .then(response => {
          Promise.all(
            saveTBConfigs(
              false,
              [],
              values.termBaseAssignments,
              response.id,
              values.targetLocales ?? []
            )
              .concat(createReferences(false, values.references, response.id)) // TODO: Need to copy references from project
              .concat(
                saveTMConfig(
                  false,
                  workflowSteps.filter(wfs => values.workflowStepIds.includes(wfs.id)),
                  [],
                  values.translationMemoryAssignments,
                  response.id,
                  values.targetLocales
                )
              )
          ).then(() => {
            dispatch({ type: 'projectTemplateCreated' });
            history.push(`/web/projectTemplate/list`);
          });
        })
        .catch(err => {
          publishCountMetric(
            'projectTemplateCreationFailed-ProjectTemplateCreate',
            'error',
            err.message
          );
          dispatch({
            type: 'projectTemplateCreationFailed',
            error: I18n.t('Failed to create project template: %{e}', {
              e: JSON.stringify(err),
            }),
          });
        })
        .finally(() => {
          const finalTime = new Date();
          publishLatencyMetric('projectTemplateCreate-Latency', initialTime, finalTime);
        });
    };

    /**
     * Per AWS Specifications, only start validating after the initial submit.
     * @param e the event
     * @param errors
     */
    const handleStepChange = (e: CustomEvent<Wizard.StepClickDetail>, errors): void => {
      if (Object.values(errors).length > 0) {
        e.preventDefault();
      } else {
        dispatch({ type: 'activeStepChange', activeStep: e.detail.requestedStepIndex });
      }
      setValidating(true);
    };

    /**
     * Per AWS Specifications, only start validating after the initial submit.
     * @param e the event
     * @param handleSubmit formik submit event handler function.
     */
    const handleSubmitWithValidation = (e: CustomEvent<Button.ClickDetail>, handleSubmit): void => {
      e.preventDefault();
      setValidating(true);
      handleSubmit();
    };

    const renderForm = (props: FormikProps<ProjectTemplateCreateAPI>): ReactElement => {
      const steps: WizardStepWithSummary[] = [
        ProjectCreateStep1Metadata({
          validating,
          ...props,
          isLoadingActiveLanguages,
          isLoadingMediaTypes,
          isLoadingSubjects,
          isLoadingWorkflowSteps,
          isLoadingScoringModels,
          isLoadingClients,
          isLoadingOwners,
          isLoadingBusinessUnits,
          isLoadingCostCenters,
          workflowSteps,
          workflowStepsById,
          scoringModels,
          scoringModelsById,
          mediaTypes,
          mediaTypesById,
          subjects,
          subjectsById,
          clients,
          clientsById,
          owners,
          ownersById,
          businessUnits,
          businessUnitsById,
          costCenters,
          costCentersById,
          activeLanguages,
          activeLanguagesByCode,
          showProjectTemplateFields: true,
        }),
        ProjectCreateStep2Assets({
          validating,
          ...props,
          isProjectMode: true,
          uid: projectUID,
          workflowSteps,
        }),
        ProjectCreateStep3Pretranslate({
          validating,
          ...props,
          isLoadingMachineTranslateSettings,
          isLoadingActiveLanguages,
          machineTranslateSettings,
          machineTranslateSettingsById,
          activeLanguages,
          activeLanguagesByCode,
        }),
        ProjectCreateStep4Analysis({ validating, ...props }),
        ProjectCreateStep5Quality({ validating, ...props }),
        ProjectCreateStep6AccessAndAutomation({ validating, ...props }),
        JobCreateStep2AssignJobs({
          session,
          validating,
          isProjectTemplateMode: true,
          isLoadingActiveLanguages,
          isLoadingWorkflowSteps,
          isLoadingUsers,
          workflowSteps,
          workflowStepsById,
          activeLanguages,
          activeLanguagesByCode,
          foundUsers,
          ...props,
        }),
        JobCreateStep3FileImporting({
          validating,
          isLoadingSupportedFileFormats,
          isLoadingSupportedFileEncodings,
          isLoadingSegmentationRules,
          supportedFileFormats,
          supportedFileEncodings,
          segmentationRules,
          isProjectTemplateMode: true,
          ...props,
        }),
      ];
      const { handleSubmit, errors } = props;

      return (
        <form>
          {(isError || usersLoadError) && (
            <Alert
              id={'createProjectTemplateError'}
              type="error"
              content={combinedError}
              dismissible={isError}
              onDismiss={(): void => {
                if (isError) {
                  dispatch({ type: 'clearError' });
                }
              }}
            />
          )}
          <WizardWithSummary
            steps={[
              ...steps,
              ProjectCreateSummary({
                ...props,
                children: steps.map(s =>
                  s.summary({
                    currentValue: project,
                    currentTranslationMemoryAssignments: translationMemoryAssignments,
                    currentTermBaseAssignments: termBaseAssignments,
                    currentReferences: references,
                  })
                ),
                title: I18n.t('Review and save'),
                helpId: PROJECT_CREATE_REVIEW_AND_SAVE_HELP,
                description: I18n.t(
                  'Review the project settings before saving the template. This view shows only fields that have changed.'
                ),
              }),
            ]}
            i18nStrings={{
              stepNumberLabel: (stepNumber: number): string =>
                I18n.t('Step %{stepNumber}', { stepNumber: stepNumber }),
              collapsedStepsLabel: (stepNumber: number, stepsCount: number): string =>
                I18n.t('Step %{stepNumber} of %{stepsCount}', {
                  stepNumber: stepNumber,
                  stepsCount: stepsCount,
                }),
              cancelButton: I18n.t('Cancel'),
              previousButton: I18n.t('Previous'),
              nextButton: I18n.t('Next'),
              submitButton: I18n.t('Create project template'),
              optional: I18n.t('Optional'),
            }}
            onSubmitButtonClick={() => handleSubmit()}
            onCancelButtonClick={() => history.goBack()}
            onNextButtonClick={e => handleStepChange(e, errors)}
            onPreviousButtonClick={e => handleStepChange(e, errors)}
            onStepNavigationClick={e => handleStepChange(e, errors)}
            additionalButtons={[
              activeStep > 0 && activeStep < steps.length && (
                <Button
                  id={'reviewAndCreate'}
                  key="reviewAndCreate"
                  formAction="none"
                  onClick={(): void =>
                    handleStepChange(
                      new CustomEvent<Wizard.StepClickDetail>('stepClickDetail', {
                        detail: { requestedStepIndex: steps.length },
                      }),
                      errors
                    )
                  }
                >
                  {I18n.t('Review and create')}
                </Button>
              ),
              activeStep === 0 && (
                <Button
                  id="create"
                  key="create"
                  formAction="none"
                  onClick={e => handleSubmitWithValidation(e, handleSubmit)}
                  loading={isCreatingProjectTemplate}
                >
                  {I18n.t('Create project template')}
                </Button>
              ),
            ].filter(b => b)}
            isSubmitting={isCreatingProjectTemplate}
            isLoadingNextStep={isCreatingProjectTemplate}
            activeStepIndex={activeStep}
          />
        </form>
      );
    };

    const initialFormValues = {
      ...initialFormValuesStep1(true, project, session),
      ...initialFormValuesStep2(termBaseAssignments, translationMemoryAssignments, references),
      ...initialFormValuesStep3(
        project,
        getDefaultMachineTranslationEngine(session, machineTranslateSettings)
      ),
      ...initialFormValuesStep4(project),
      ...initialFormValuesStep5(project),
      ...initialFormValuesStep6(project),
      ...jobCreateInitialFormValuesStep2(project),
      ...jobCreateInitialFormValuesStep3(projectTemplate, project),
    };
    const schema = yup
      .object()
      .concat(schemaStep1(false))
      .concat(schemaStep2)
      .concat(schemaStep3)
      .concat(schemaStep4)
      .concat(schemaStep5)
      .concat(schemaStep6)
      .concat(jobCreateSchemaStep2)
      .concat(jobCreateSchemaStep3);

    return !isLoadingProject &&
      !isLoadingProjectTemplate &&
      !isLoadingTermBaseAssignments &&
      !isLoadingTransMemoryAssignments &&
      !isLoadingReferences ? (
      <PolarisFormik
        initialValues={initialFormValues}
        validationSchema={schema}
        validateOnChange={true}
        onSubmit={handleSubmit}
        render={renderForm}
      />
    ) : (
      <Spinner size="large" />
    );
  }
);
